import axios from 'axios';
import config from '../config/environment';

class NetworkService {
  constructor() {
    this.api = axios.create({
      baseURL: config.apiUrl,
      timeout: config.timeout,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Enable debug logging in development
    if (config.debug) {
      this.api.interceptors.request.use(request => {
        console.log('Starting Request:', request);
        return request;
      });

      this.api.interceptors.response.use(response => {
        console.log('Response:', response);
        return response;
      });
    }

    // Request interceptor
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor
    this.api.interceptors.response.use(
      (response) => response.data,
      (error) => {
        if (error.response?.status === 401) {
          // Handle unauthorized access
          localStorage.removeItem('token');
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  // Auth endpoints
  async login(credentials) {
    return this.api.post('/auth/login', credentials);
  }

  async register(userData) {
    return this.api.post('/auth/register', userData);
  }

  async forgotPassword(email) {
    return this.api.post('/auth/forgot-password', { email });
  }

  async resetPassword(token, newPassword) {
    return this.api.post('/auth/reset-password', { token, newPassword });
  }

  // User endpoints
  async getCurrentUser() {
    return this.api.get('/user/me');
  }

  async updateProfile(userData) {
    return this.api.put('/user/profile', userData);
  }

  // Business Idea endpoints
  async createIdea(ideaData) {
    return this.api.post('/ideas', ideaData);
  }

  async getIdeas() {
    return this.api.get('/ideas');
  }

  async getIdeaById(id) {
    return this.api.get(`/ideas/${id}`);
  }

  async updateIdea(id, ideaData) {
    return this.api.put(`/ideas/${id}`, ideaData);
  }

  async deleteIdea(id) {
    return this.api.delete(`/ideas/${id}`);
  }

  // Market Research endpoints
  async getMarketAnalysis(params) {
    return this.api.get('/market-research/analysis', { params });
  }

  async getCompetitors(ideaId) {
    return this.api.get(`/market-research/competitors/${ideaId}`);
  }

  // Cost Estimation endpoints
  async generateCostEstimate(projectData) {
    return this.api.post('/cost-estimation', projectData);
  }

  async getCostEstimates() {
    return this.api.get('/cost-estimation');
  }

  // Report endpoints
  async generateReport(ideaId, reportType) {
    return this.api.post('/reports/generate', { ideaId, reportType });
  }

  async getReports() {
    return this.api.get('/reports');
  }

  // Waitlist endpoints
  async joinWaitlist(userData) {
    console.log('Joining waitlist:', userData);
    return this.api.post('/vip/waitlist', userData);
  }

  // Error handler
  handleError(error) {
    const errorMessage = error.response?.data?.message || 'Something went wrong';
    console.error('API Error:', error);
    throw new Error(errorMessage);
  }

  // Generic request methods
  async get(endpoint, params = {}) {
    try {
      return await this.api.get(endpoint, { params });
    } catch (error) {
      this.handleError(error);
    }
  }

  async post(endpoint, data = {}) {
    try {
      return await this.api.post(endpoint, data);
    } catch (error) {
      this.handleError(error);
    }
  }

  async put(endpoint, data = {}) {
    try {
      return await this.api.put(endpoint, data);
    } catch (error) {
      this.handleError(error);
    }
  }

  async delete(endpoint) {
    try {
      return await this.api.delete(endpoint);
    } catch (error) {
      this.handleError(error);
    }
  }
}

const networkService = new NetworkService();
export default networkService; 